import { useContext, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MuiIconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// auth + firestore
import { useAuth } from 'lib/auth';
import { getDocFromServer, onSnapshot, query, where } from 'firebase/firestore';
import { eventCollection, eventDoc } from '../../../../event/paths';
import { EventContext } from '../../../../event/eventContext';
import { db } from 'lib/firebase';
import {
  isEventbritePayout,
  payoutHidesCollectionNotice,
  paymentNoticeBody,
  retainerFromServerDoc,
  personalPayoutView,
  retainerReadKey,
  collectNoticeAllowed,
  reconcilePopupItems
} from '../../../../utils/ticketedEventsUi';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DollarOutlined from '@ant-design/icons/DollarOutlined';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import NotificationOutlined from '@ant-design/icons/NotificationOutlined';

// ─── localStorage helpers ──────────────────────────────────────────────────────

const storageKey = (eventId, uid) => `guestlist_notif_${eventId || 'club-astra'}_${uid}`;

function loadPersistedState(eventId, uid) {
  try {
    const raw = localStorage.getItem(storageKey(eventId, uid));
    if (!raw) return { seenIds: [], dismissedIds: [] };
    return JSON.parse(raw);
  } catch {
    return { seenIds: [], dismissedIds: [] };
  }
}

function persistState(eventId, uid, seenIds, dismissedIds) {
  try {
    localStorage.setItem(storageKey(eventId, uid), JSON.stringify({ seenIds: [...seenIds], dismissedIds: [...dismissedIds] }));
  } catch {}
}

// ─── Role helpers ──────────────────────────────────────────────────────────────

function getAudienceFromProfile(profile) {
  const raw = (profile?.role || profile?.type || '').toLowerCase().trim();
  if (!raw) return null;
  if (raw.includes('head')) return 'Head Host';
  if (raw === 'host') return 'Host';
  if (raw === 'ambassador') return 'Ambassador';
  if (raw === 'management' || raw === 'manager') return 'Management';
  if (raw === 'admin' || raw === 'administrator') return 'Admin';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function getPopupRoleFromProfile(profile) {
  const raw = (profile?.role || profile?.type || '').toLowerCase().trim();
  if (!raw) return null;
  if (raw.includes('head')) return 'head_host';
  if (raw === 'host') return 'host';
  if (raw === 'ambassador') return 'ambassador';
  if (raw === 'management' || raw === 'manager') return 'management';
  if (raw === 'admin' || raw === 'administrator') return 'admin';
  return raw.replace(/\s+/g, '_');
}

// ─── Time formatters ───────────────────────────────────────────────────────────

const formatTime = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatRelative = (date) => {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

// ─── Payout helpers ────────────────────────────────────────────────────────────

const PAYOUT_CUTOFF = '2026-05-01';

// Emergency kill switch for all payment notifications. Eligibility below
// separately excludes every payout containing an electronic retainer.
const PAYMENT_NOTIFICATIONS_ENABLED = true;

function formatPayoutMoney(val) {
  if (val == null || isNaN(val)) return '$0.00';
  return `$${Number(val).toFixed(2)}`;
}

function payoutNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function hasElectronicRetainer(p) {
  return payoutNumber(p.retainerAmount ?? p.breakdown?.retainer?.amount) > 0;
}

function isEligiblePayout(p) {
  return (
    (p.status === 'approved' || p.status === 'paid') &&
    String(p.eventDay || '') >= PAYOUT_CUTOFF &&
    !hasElectronicRetainer(p)
  );
}

function getPayoutDate(p) {
  if (p.updatedAt?.toDate) return p.updatedAt.toDate();
  if (p.createdAt?.toDate) return p.createdAt.toDate();
  return new Date();
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatEventDay(dateStr) {
  if (!dateStr) return dateStr;
  const [year, month, day] = dateStr.split('-');
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
}

// ─── Per-type icon + colour config ────────────────────────────────────────────

const TYPE_META = {
  announcement: { icon: <GiftOutlined />,          color: 'primary.main',  bg: 'primary.lighter'  },
  popup:        { icon: <NotificationOutlined />,   color: 'warning.main',  bg: 'warning.lighter'  },
  event:        { icon: <CalendarOutlined />,        color: 'success.main',  bg: 'success.lighter'  },
  payment:      { icon: <DollarOutlined />,          color: 'error.main',    bg: 'error.lighter'    }
};

const avatarSX = { width: 36, height: 36, fontSize: '1rem' };

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const ev = useContext(EventContext);
  const eventId = ev?.eventId || 'club-astra';
  const { user, profile } = useAuth();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Separate state per source — merged in useMemo
  const [announcements, setAnnouncements] = useState([]);
  const [popups, setPopups] = useState([]);
  const [specialEvents, setSpecialEvents] = useState([]);
  // Personal payouts, tagged with the profile id they were loaded for.
  const [personalSnap, setPersonalSnap] = useState({ id: null, payouts: [] });
  const [teamPaymentNotifs, setTeamPaymentNotifs] = useState([]);

  // Persisted read/dismiss state (drives badge count only)
  const [seenIds, setSeenIds] = useState(new Set());
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [seenIdsInitialized, setSeenIdsInitialized] = useState(false);
  // Visual unread state inside the open dropdown — captured at open time
  const [unreadInDropdown, setUnreadInDropdown] = useState(new Set());

  // Payment popup state
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [popupPersonal, setPopupPersonal] = useState([]);
  const [popupTeam, setPopupTeam] = useState([]);
  const popupFiredForUid = useRef(null);

  // Track when each payment query has received its first response
  const [teamLoaded, setTeamLoaded] = useState(false);

  // Role check for team payments
  const roleRaw = (profile?.role || profile?.type || '').toLowerCase();
  const isTeamManager =
    roleRaw === 'head host' || roleRaw === 'head_host' || roleRaw === 'headhost' ||
    roleRaw === 'ambassador' || roleRaw === 'management' || roleRaw === 'admin';

  // ── Restore persisted state when user changes ─────────────────────────────────
  useEffect(() => {
    if (!user?.uid) {
      setSeenIds(new Set());
      setDismissedIds(new Set());
      setSeenIdsInitialized(false);
      setTeamLoaded(false);
      return;
    }
    const saved = loadPersistedState(eventId, user.uid);
    setSeenIds(new Set(saved.seenIds || []));
    setDismissedIds(new Set(saved.dismissedIds || []));
    setSeenIdsInitialized(true);
  }, [eventId, user?.uid]);

  // ── Source 1: Announcements ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setAnnouncements([]); return; }

    const audience = getAudienceFromProfile(profile);
    const audienceFilters = audience ? [audience, 'All'] : ['All'];
    const q = query(eventCollection(db, 'announcements'), where('audience', 'array-contains-any', audienceFilters));

    return onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => {
          const data = d.data();
          let createdAt;
          if (data.createdAt?.toDate) createdAt = data.createdAt.toDate();
          else if (typeof data.createdAt === 'string') createdAt = new Date(data.createdAt);
          else createdAt = new Date();
          return { id: `ann_${d.id}`, type: 'announcement', title: data.title || 'Announcement', body: data.body || '', createdAt };
        });
        setAnnouncements(items);
      },
      (err) => { console.error('Announcements listener error:', err); setAnnouncements([]); }
    );
  }, [user, profile?.role, profile?.type]);

  // ── Source 2: App Popups ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setPopups([]); return; }

    const popupRole = getPopupRoleFromProfile(profile);
    const roleFilters = popupRole ? [popupRole, 'all'] : ['all'];
    const q = query(eventCollection(db, 'appPopups'), where('isActive', '==', true), where('roles', 'array-contains-any', roleFilters));

    return onSnapshot(
      q,
      (snap) => {
        const now = new Date();
        const items = snap.docs.flatMap((d) => {
          const data = d.data();
          const startsAt = data.startsAt?.toDate ? data.startsAt.toDate() : null;
          const endsAt = data.endsAt?.toDate ? data.endsAt.toDate() : null;
          if (startsAt && now < startsAt) return [];
          if (endsAt && now > endsAt) return [];

          let createdAt;
          if (data.createdAt?.toDate) createdAt = data.createdAt.toDate();
          else if (typeof data.createdAt === 'string') createdAt = new Date(data.createdAt);
          else createdAt = new Date();

          return [{ id: `popup_${d.id}`, type: 'popup', title: data.title || 'Notice', body: data.body || '', createdAt }];
        });
        setPopups(items);
      },
      (err) => { console.error('App Popups listener error:', err); setPopups([]); }
    );
  }, [user, profile?.role, profile?.type]);

  // ── Source 3: Active Special Events (via meta/uiSettings) ────────────────────
  useEffect(() => {
    if (!user) { setSpecialEvents([]); return; }

    return onSnapshot(
      eventDoc(db, 'meta', 'uiSettings'),
      (snap) => {
        const data = snap.data();
        if (!data?.specialEventsMenuEnabled) { setSpecialEvents([]); return; }

        const active = data?.activeSpecialEvents || [];
        const updatedAt = data?.activeSpecialEventsUpdatedAt?.toDate
          ? data.activeSpecialEventsUpdatedAt.toDate()
          : new Date();

        const items = active.map((event) => ({
          id: `event_${event.id}`,
          type: 'event',
          title: event.guestlistName || event.name || 'Special Event',
          body: event.body || `Event date: ${event.eventDay || 'TBA'}`,
          createdAt: updatedAt
        }));

        setSpecialEvents(items);
      },
      (err) => { console.error('Special Events listener error:', err); setSpecialEvents([]); }
    );
  }, [user]);

  // Retainer status for Eventbrite ticket commission, confirmed from the server
  // (the profile may first paint from cache). Until confirmed, no Eventbrite
  // "ready for collection" notice is shown; weekly payouts are unaffected.
  // `settled` = the server check finished (answered or failed), so the pop-up
  // can wait for it instead of firing without a notice that is about to appear.
  // Each answer is tagged with the key it was read for (profile id + retainer
  // amount), so an answer for an older retainer amount is never reused.
  const readKey = retainerReadKey(profile);
  const [serverRetainer, setServerRetainer] = useState({ key: null, onRetainer: null, settled: false });
  useEffect(() => {
    if (!readKey) {
      setServerRetainer({ key: null, onRetainer: null, settled: false });
      return undefined;
    }
    let cancelled = false;
    setServerRetainer({ key: readKey, onRetainer: null, settled: false });
    getDocFromServer(eventDoc(db, 'users', profile.id))
      .then((snap) => {
        // A missing doc stays unconfirmed (null), never "cash".
        if (!cancelled) setServerRetainer({ key: readKey, onRetainer: retainerFromServerDoc(snap), settled: true });
      })
      .catch(() => {
        if (!cancelled) setServerRetainer({ key: readKey, onRetainer: null, settled: true });
      });
    return () => {
      cancelled = true;
    };
  }, [readKey]); // eslint-disable-line react-hooks/exhaustive-deps -- readKey encodes profile.id + retainerAmount

  // ── Source 4a: Personal payments ──────────────────────────────────────────────
  useEffect(() => {
    if (!PAYMENT_NOTIFICATIONS_ENABLED || !user || !profile?.id) {
      setPersonalSnap({ id: null, payouts: [] });
      return;
    }
    const forId = profile.id;

    const q = query(
      eventCollection(db, 'payouts'),
      where('userId', '==', profile.id)
    );

    return onSnapshot(
      q,
      (snap) => {
        setPersonalSnap({ id: forId, payouts: snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(isEligiblePayout) });
      },
      (err) => {
        console.error('Personal payments listener error:', err);
        setPersonalSnap({ id: forId, payouts: [] });
      }
    );
  }, [user, profile?.id]);

  // Derived (not stored) so a change in retainer status re-filters at once.
  // Results loaded for a previous profile (e.g. while switching host) are
  // ignored until the current profile's own results arrive (Codex R10).
  const personalView = personalPayoutView(
    PAYMENT_NOTIFICATIONS_ENABLED && user ? profile?.id : null,
    personalSnap,
    serverRetainer,
    readKey
  );
  const personalLoaded = personalView.loaded;
  const retainerSettled = personalView.settled;
  const { onRetainer: viewOnRetainer, payouts: viewPayouts } = personalView;
  const personalPaymentNotifs = useMemo(() => {
    return (
      viewPayouts
        // Eventbrite ticket commission: only once the host is confirmed NOT on a
        // retainer (retainer hosts are paid by bank transfer, never at reception).
        .filter((p) => collectNoticeAllowed(p, viewOnRetainer))
        .map((p) => ({
          id: `pay_${p.id}`,
          type: 'payment',
          source: 'personal',
          title: 'New Host Payment Available',
          body: paymentNoticeBody(p, formatPayoutMoney(p.totalAmount), formatEventDay(p.eventDay)),
          createdAt: getPayoutDate(p),
          amount: p.totalAmount,
          eventDay: p.eventDay,
          eventbrite: isEventbritePayout(p)
        }))
    );
  }, [viewPayouts, viewOnRetainer]);

  // ── Source 4b: Team payments (managers only) ──────────────────────────────────
  useEffect(() => {
    if (!PAYMENT_NOTIFICATIONS_ENABLED || !user || !profile?.userId || !isTeamManager) {
      setTeamPaymentNotifs([]);
      setTeamLoaded(true); // No team query needed — unblock the popup check
      return;
    }

    setTeamLoaded(false); // Reset while new query is starting

    const q = query(
      eventCollection(db, 'payouts'),
      where('managerUserId', '==', profile.userId)
    );

    return onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => isEligiblePayout(p) && !payoutHidesCollectionNotice(p))
          .map((p) => ({
            id: `pay_${p.id}`,
            type: 'payment',
            source: 'team',
            title: `Team Payment: ${p.userName || 'Team Member'}`,
            body: isEventbritePayout(p)
              ? paymentNoticeBody(p, formatPayoutMoney(p.totalAmount), formatEventDay(p.eventDay))
              : `${formatPayoutMoney(p.totalAmount)} for ${formatEventDay(p.eventDay)} is ready for collection.`,
            createdAt: getPayoutDate(p),
            amount: p.totalAmount,
            eventDay: p.eventDay,
            userName: p.userName || 'Team Member'
          }));
        setTeamPaymentNotifs(items);
        setTeamLoaded(true);
      },
      (err) => { console.error('Team payments listener error:', err); setTeamPaymentNotifs([]); setTeamLoaded(true); }
    );
  }, [user, profile?.userId, isTeamManager]);

  // ── Payment popup: fire once per session when unseen payments exist ────────────
  const paymentNotifs = useMemo(
    () => [...personalPaymentNotifs, ...teamPaymentNotifs],
    [personalPaymentNotifs, teamPaymentNotifs]
  );

  useEffect(() => {
    if (!user?.uid || !seenIdsInitialized) return;
    if (!personalLoaded || !teamLoaded) return; // Wait for both queries to respond
    if (!retainerSettled) return; // and for the server's answer on retainer status
    const popupKey = `${eventId}:${user.uid}`;
    if (popupFiredForUid.current === popupKey) return;

    const newPersonal = personalPaymentNotifs.filter((n) => !seenIds.has(n.id));
    const newTeam = teamPaymentNotifs.filter((n) => !seenIds.has(n.id));

    if (newPersonal.length === 0 && newTeam.length === 0) return;

    popupFiredForUid.current = popupKey;
    setPopupPersonal(newPersonal);
    setPopupTeam(newTeam);
    setShowPaymentPopup(true);
  }, [
    eventId,
    personalPaymentNotifs,
    teamPaymentNotifs,
    personalLoaded,
    teamLoaded,
    retainerSettled,
    seenIds,
    seenIdsInitialized,
    user?.uid
  ]);

  // Keep an open payment pop-up in step with eligibility (e.g. the live profile
  // arrives after a cached one): drop items no longer eligible, close if empty.
  useEffect(() => {
    if (!showPaymentPopup) return;
    const personal = reconcilePopupItems(popupPersonal, personalPaymentNotifs);
    const team = reconcilePopupItems(popupTeam, teamPaymentNotifs);
    if (personal.length !== popupPersonal.length) setPopupPersonal(personal);
    if (team.length !== popupTeam.length) setPopupTeam(team);
    if (personal.length === 0 && team.length === 0) setShowPaymentPopup(false);
  }, [showPaymentPopup, personalPaymentNotifs, teamPaymentNotifs, popupPersonal, popupTeam]);

  // ── Merge + filter ────────────────────────────────────────────────────────────
  const visibleNotifications = useMemo(
    () =>
      [...announcements, ...popups, ...specialEvents, ...paymentNotifs]
        .filter((n) => !dismissedIds.has(n.id))
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 15),
    [announcements, popups, specialEvents, paymentNotifs, dismissedIds]
  );

  const unreadCount = useMemo(
    () => visibleNotifications.filter((n) => !seenIds.has(n.id)).length,
    [visibleNotifications, seenIds]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleToggle = () => {
    setOpen((prev) => {
      if (!prev) {
        const currentUnread = new Set(visibleNotifications.filter((n) => !seenIds.has(n.id)).map((n) => n.id));
        setUnreadInDropdown(currentUnread);

        if (user?.uid) {
          const nextSeen = new Set([...seenIds, ...visibleNotifications.map((n) => n.id)]);
          setSeenIds(nextSeen);
          persistState(eventId, user.uid, nextSeen, dismissedIds);
        }
      }
      return !prev;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
    setUnreadInDropdown(new Set());
  };

  const handleMarkAllRead = () => {
    setUnreadInDropdown(new Set());
    if (!user?.uid) return;
    const nextSeen = new Set([...seenIds, ...visibleNotifications.map((n) => n.id)]);
    setSeenIds(nextSeen);
    persistState(eventId, user.uid, nextSeen, dismissedIds);
  };

  const handleDismiss = (e, id) => {
    e.stopPropagation();
    if (!user?.uid) return;
    const nextDismissed = new Set([...dismissedIds, id]);
    const nextSeen = new Set([...seenIds, id]);
    setDismissedIds(nextDismissed);
    setSeenIds(nextSeen);
    setUnreadInDropdown((prev) => { const s = new Set(prev); s.delete(id); return s; });
    persistState(eventId, user.uid, nextSeen, nextDismissed);
  };

  const handleDismissPaymentPopup = () => {
    setShowPaymentPopup(false);
    if (!user?.uid) return;
    const allIds = [...popupPersonal, ...popupTeam].map((n) => n.id);
    const nextSeen = new Set([...seenIds, ...allIds]);
    setSeenIds(nextSeen);
    persistState(eventId, user.uid, nextSeen, dismissedIds);
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 285,
                maxWidth: { xs: 285, md: 420 }
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    unreadCount > 0 && (
                      <Tooltip title="Mark all as read">
                        <IconButton color="success" size="small" onClick={handleMarkAllRead}>
                          <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <List component="nav" sx={{ p: 0 }}>
                    {visibleNotifications.length === 0 ? (
                      <ListItem sx={{ py: 2 }}>
                        <ListItemText
                          primary={<Typography variant="h6">No notifications</Typography>}
                          secondary="You are all caught up."
                        />
                      </ListItem>
                    ) : (
                      visibleNotifications.map((n) => {
                        const isUnread = unreadInDropdown.has(n.id);
                        const isExpanded = expandedId === n.id;
                        const meta = TYPE_META[n.type] || TYPE_META.announcement;
                        return (
                          <ListItem
                            key={n.id}
                            divider
                            alignItems="flex-start"
                            onClick={() => {
                              setExpandedId((prev) => (prev === n.id ? null : n.id));
                              if (unreadInDropdown.has(n.id)) {
                                setUnreadInDropdown((prev) => { const s = new Set(prev); s.delete(n.id); return s; });
                              }
                            }}
                            sx={{
                              py: 1,
                              px: 2,
                              pr: 7,
                              cursor: 'pointer',
                              bgcolor: isUnread ? 'primary.lighter' : 'transparent',
                              transition: 'background-color 0.2s',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                            secondaryAction={
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, pt: 0.5 }}>
                                <Typography variant="caption" noWrap color="text.secondary">
                                  {formatTime(n.createdAt)}
                                </Typography>
                                <Tooltip title="Dismiss">
                                  <MuiIconButton
                                    size="small"
                                    onClick={(e) => handleDismiss(e, n.id)}
                                    sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                                  >
                                    <CloseOutlined style={{ fontSize: '0.7rem' }} />
                                  </MuiIconButton>
                                </Tooltip>
                              </Box>
                            }
                          >
                            <ListItemAvatar>
                              <Badge
                                variant="dot"
                                color="primary"
                                invisible={!isUnread}
                                overlap="circular"
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <Avatar sx={{ ...avatarSX, color: meta.color, bgcolor: meta.bg }}>
                                  {meta.icon}
                                </Avatar>
                              </Badge>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="h6"
                                  noWrap={!isExpanded}
                                  fontWeight={isUnread ? 700 : 400}
                                  color={isUnread ? 'text.primary' : 'text.secondary'}
                                >
                                  {n.title}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: 'block',
                                      ...(isExpanded
                                        ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                                        : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })
                                    }}
                                  >
                                    {n.body}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatRelative(n.createdAt)}
                                    {!isExpanded && n.body && n.body.length > 40 && (
                                      <Box component="span" sx={{ ml: 0.5, color: 'primary.main', fontWeight: 500 }}>
                                        tap to read more
                                      </Box>
                                    )}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* ── Payment notification popup ────────────────────────────────────────── */}
      <Dialog open={showPaymentPopup} onClose={handleDismissPaymentPopup} maxWidth="sm" fullWidth>
        <DialogTitle>
          {popupPersonal.length > 0 && popupTeam.length > 0
            ? 'New Payments Available'
            : popupPersonal.length > 0
            ? 'New Host Payment Available'
            : `Team Payment${popupTeam.length > 1 ? 's' : ''} Available`}
        </DialogTitle>
        <DialogContent dividers>
          {/* Personal section */}
          {popupPersonal.length > 0 && (
            <Box sx={{ mb: popupTeam.length > 0 ? 2 : 0 }}>
              {popupTeam.length > 0 && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Your Payment
                </Typography>
              )}
              {popupPersonal.map((p) => (
                <Typography key={p.id} variant="body2" sx={{ mb: 0.5 }}>
                  {p.eventbrite
                    ? p.body
                    : `Your host payment of ${formatPayoutMoney(p.amount)} for ${formatEventDay(p.eventDay)} is now ready for collection.`}
                </Typography>
              ))}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please collect your payment from reception on Friday.
              </Typography>
            </Box>
          )}

          {/* Divider between sections when both are present */}
          {popupPersonal.length > 0 && popupTeam.length > 0 && <Divider sx={{ my: 2 }} />}

          {/* Team section */}
          {popupTeam.length > 0 && (
            <Box>
              {popupPersonal.length > 0 && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Team Payments
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1 }}>
                The following team member{popupTeam.length > 1 ? 's have' : ' has'} a payment ready for collection:
              </Typography>
              {popupTeam.map((p) => (
                <Typography key={p.id} variant="body2" sx={{ mb: 0.25 }}>
                  • {p.userName} — {formatPayoutMoney(p.amount)} for {formatEventDay(p.eventDay)}
                </Typography>
              ))}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please notify them to collect their payment from reception on Friday.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleDismissPaymentPopup}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
